import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { t } from "../data/translations";
import styles from "./Services.module.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const Services = () => {
  const { lang } = useLang();

  const plans = [
    {
      tier: "bronze",
      name: t("services.bronzeName", lang),
      title: t("services.bronzeTitle", lang),
      target: t("services.bronzeTarget", lang),
      desc: t("services.bronzeDesc", lang),
      features: t("services.bronzeFeatures", lang),
      cta: t("services.bronzeCta", lang),
      accent: "#cd7f32",
    },
    {
      tier: "silver",
      name: t("services.silverName", lang),
      title: t("services.silverTitle", lang),
      target: t("services.silverTarget", lang),
      desc: t("services.silverDesc", lang),
      features: t("services.silverFeatures", lang),
      cta: t("services.silverCta", lang),
      badge: t("services.silverBadge", lang),
      accent: "#c0c0c0",
      featured: true,
    },
    {
      tier: "gold",
      name: t("services.goldName", lang),
      title: t("services.goldTitle", lang),
      target: t("services.goldTarget", lang),
      desc: t("services.goldDesc", lang),
      features: t("services.goldFeatures", lang),
      cta: t("services.goldCta", lang),
      accent: "#ffd700",
    },
  ];

  const tableFeatures = [
    { key: "feat1", bronze: true, silver: true, gold: true },
    { key: "feat2", bronze: true, silver: true, gold: true },
    { key: "feat3", bronze: true, silver: true, gold: true },
    { key: "feat4", bronze: true, silver: true, gold: true },
    { key: "feat5", bronze: true, silver: true, gold: true },
    { key: "feat6", bronze: true, silver: true, gold: true },
    { key: "feat7", bronze: false, silver: true, gold: true },
    { key: "feat8", bronze: false, silver: true, gold: true },
    { key: "feat9", bronze: false, silver: true, gold: true },
    { key: "feat10", bronze: false, silver: true, gold: true },
    { key: "feat11", bronze: false, silver: false, gold: true },
    { key: "feat12", bronze: false, silver: false, gold: true },
    { key: "feat13", bronze: false, silver: false, gold: true },
    { key: "feat14", bronze: false, silver: false, gold: true },
  ];

  return (
    <div className={styles.wrapper}>
      {/* Hero */}
      <motion.section
        className={styles.heroArea}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className={styles.title}>{t("services.title", lang)}</h1>
        <p className={styles.subtitle}>{t("services.subtitle", lang)}</p>
      </motion.section>

      {/* Pricing Cards */}
      <motion.section
        className={styles.pricingSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <div className={styles.plansGrid}>
          {plans.map((plan) => (
            <motion.div
              key={plan.tier}
              className={`${styles.planCard} ${plan.featured ? styles.featured : ""}`}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              style={{ "--plan-accent": plan.accent }}
            >
              {plan.badge && (
                <span className={styles.popularBadge}>{plan.badge}</span>
              )}
              <span className={styles.planTier} style={{ color: plan.accent }}>
                {plan.name}
              </span>
              <h3 className={styles.planTitle}>{plan.title}</h3>
              <p className={styles.planTarget}>{plan.target}</p>
              <p className={styles.planDesc}>{plan.desc}</p>
              <ul className={styles.planFeatures}>
                {plan.features.map((feat, i) => (
                  <li key={i}>
                    <span className={styles.checkMark} style={{ color: plan.accent }}>✓</span>
                    {feat}
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={styles.planCta}
                style={{ backgroundColor: plan.accent, color: "#1a1a1a" }}
              >
                {plan.cta}
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Comparison Table */}
      <motion.section
        className={styles.tableSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <motion.div className={styles.tableWrapper} variants={fadeUp}>
          <table className={styles.comparisonTable}>
            <thead>
              <tr>
                <th>{t("services.tableHeader", lang)}</th>
                <th style={{ color: "#cd7f32" }}>Bronze</th>
                <th style={{ color: "#c0c0c0" }}>Silver</th>
                <th style={{ color: "#ffd700" }}>Gold</th>
              </tr>
            </thead>
            <tbody>
              {tableFeatures.map((feat, i) => (
                <motion.tr
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.03, duration: 0.3 }}
                >
                  <td className={styles.featureName}>
                    {t(`services.${feat.key}`, lang)}
                  </td>
                  <td className={styles.checkCell}>
                    {feat.bronze ? (
                      <span className={styles.check}>✓</span>
                    ) : (
                      <span className={styles.dash}>—</span>
                    )}
                  </td>
                  <td className={styles.checkCell}>
                    {feat.silver ? (
                      <span className={styles.check}>✓</span>
                    ) : (
                      <span className={styles.dash}>—</span>
                    )}
                  </td>
                  <td className={styles.checkCell}>
                    {feat.gold ? (
                      <span className={styles.check}>✓</span>
                    ) : (
                      <span className={styles.dash}>—</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </motion.section>

      {/* Who Is This For */}
      <motion.section
        className={styles.whoSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <motion.h2 className={styles.whoTitle} variants={fadeUp}>
          {t("services.whoTitle", lang)}
        </motion.h2>
        <motion.p className={styles.whoSubtitle} variants={fadeUp}>
          {t("services.whoSubtitle", lang)}
        </motion.p>
        <div className={styles.whoGrid}>
          {["who1", "who2", "who3", "who4", "who5", "who6", "who7", "who8"].map((key, i) => (
            <motion.div key={i} className={styles.whoTag} variants={fadeUp}>
              {t(`services.${key}`, lang)}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Personal Section */}
      <motion.section
        className={styles.personalSection}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <div className={styles.personalCard}>
          <div className={styles.personalAvatar}>
            <span>A</span>
          </div>
          <h3 className={styles.personalTitle}>{t("services.personalTitle", lang)}</h3>
          <blockquote className={styles.personalQuote}>
            "{t("services.personalQuote", lang)}"
          </blockquote>
        </div>
      </motion.section>

      {/* Audit Section */}
      <motion.section
        className={styles.auditSection}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
      >
        <div className={styles.auditCard}>
          <h3>{t("services.auditTitle", lang)}</h3>
          <p>{t("services.auditDesc", lang)}</p>
          <Link to="/contact" className={styles.auditCta}>
            {t("services.auditCta", lang)}
          </Link>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section
        className={styles.ctaSection}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        variants={staggerContainer}
      >
        <motion.div className={styles.ctaContent} variants={fadeUp}>
          <h2>{t("services.ctaTitle", lang)}</h2>
          <p>{t("services.ctaDesc", lang)}</p>
          <div className={styles.ctaDual}>
            <a
              href="mailto:info@myapple.gr?subject=Meeting%20in%20Thessaloniki"
              className={styles.ctaPrimary}
            >
              {t("services.ctaInPerson", lang)}
            </a>
            <a
              href="mailto:info@myapple.gr?subject=Online%20Meeting"
              className={styles.ctaOutline}
            >
              {t("services.ctaOnline", lang)}
            </a>
          </div>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default Services;
